import React from 'react';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../constants';
import { SearchFilters, SearchResultsTab } from '../../../types/search';
import useRouter from '../../../utils/useRouter';
import DashboardMuiCard from '../../cards/MuiCard/DashboardMuiCard';
import { getCommonCardProps } from '../../Search/SearchResults/SearchResults';
import { AsyncActionStatus } from '../../../actions/common.actionTypes';
import searchFiltersToQuery from '../../../utils/searchFiltersToQuery';
import useDashboardWorksheets from '../../../routes/DashboardPage/hooks/useDashboardWorksheets';
import { MIN_ITEMS } from '../../../routes/DashboardPage/hooks/useDashboardRowFetch';
import DashboardRowShell from '../DashboardRowShell/DashboardRowShell';

interface OwnProps {
  // Every configured user subject — not only the top-three display subjects.
  subjectIds: string[];
}

/**
 * Newest worksheet pages across all of the user's subjects (PRO-6). A single
 * query (subject + pageContentType=arbeitsblatt), rendered as document cards.
 * Static title, no breadcrumb. Hidden when there are no subjects, on a fetch
 * error, or when fewer than MIN_ITEMS worksheets resolve.
 */
const WorksheetRow: React.FC<OwnProps> = ({ subjectIds }) => {
  const { t } = useTranslation();
  const { router } = useRouter();

  const { items, status } = useDashboardWorksheets(subjectIds);

  const isLoading =
    status === AsyncActionStatus.INITIAL || status === AsyncActionStatus.LOADING;
  const isShort = items.length < MIN_ITEMS;

  const hidden =
    !subjectIds.length || status === AsyncActionStatus.ERROR || (!isLoading && isShort);

  const onShowAllClick = () => {
    // The destination must agree with the row: all subjects + worksheet content type.
    const searchParams: SearchFilters = {
      subject: subjectIds,
      pageContentType: ['arbeitsblatt'],
      sortBy: 'createdDate',
    };
    const qs = searchFiltersToQuery(searchParams);
    router.push(`/${routes.search.root}/${SearchResultsTab.MINDITEMS}?${qs}`);
  };

  return (
    <DashboardRowShell
      title={t('Worksheets for your subjects')}
      onShowAll={onShowAllClick}
      loading={isLoading}
      hidden={hidden}
    >
      {items.map((item) => (
        <DashboardMuiCard key={item.id} contentType='document' {...getCommonCardProps(item)} />
      ))}
    </DashboardRowShell>
  );
};

export default WorksheetRow;
